import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useChildFeedPages } from '../../hooks/useChildFeedPages';
import { useListMyChildIncidentsQuery } from '../../store/api/localApi';
import { mergeUpdates } from '../../pages/parent/updates';
import { formatDublinDate } from '../../utils/date';
import FeedItemCard from './FeedItemCard';
import IncidentSeverityChip from '../incidents/IncidentSeverityChip';

const PREVIEW_SIZE = 2;

/**
 * The child home's "Recent" preview: the newest two updates for one child,
 * drawn from the same sources as the Updates page — the feed (moments +
 * observations) and the child's incident reports — so a new incident report
 * is visible on Home rather than only on Updates.
 *
 * PASSIVE by design: it never marks anything read, so seeing an update on
 * Home does not clear a badge — the parent has to open the item.
 */
const RecentPreview: React.FC<{ childId: string }> = ({ childId }) => {
  const { items, isLoading: feedLoading } = useChildFeedPages(childId);
  const { data: incidentsData, isLoading: incidentsLoading } = useListMyChildIncidentsQuery({
    childId,
  });

  // Only the first feed page is ever loaded here, but the feed is passed as
  // "exhausted" on purpose. mergeUpdates holds incidents back while more pages
  // remain so they don't jump above items still to come; for a top-N slice that
  // can't happen — any unloaded feed item is older than every loaded one, so
  // the true newest N are always among the first page plus the incidents.
  const preview = useMemo(
    () => mergeUpdates(items, incidentsData?.incidents ?? [], true).slice(0, PREVIEW_SIZE),
    [items, incidentsData]
  );

  const base = `/parent/${childId}`;

  // Wait for both sources: rendering incidents alone and then reordering when
  // the feed lands would make the preview jump.
  if (feedLoading || incidentsLoading) {
    return (
      <section data-testid="parent-recent-feed" aria-label="Recent updates">
        <div className="bg-white rounded-lg shadow p-4 text-center text-sm text-gray-500">
          Loading…
        </div>
      </section>
    );
  }

  return (
    <section data-testid="parent-recent-feed" aria-label="Recent updates">
      {preview.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-6 text-center text-sm text-gray-500">
          No recent updates yet
        </div>
      ) : (
        <div className="space-y-2">
          {preview.map((item) =>
            item.kind === 'INCIDENT' ? (
              <Link
                key={`incident-${item.id}`}
                to={`${base}/incidents/${item.id}`}
                data-testid={`recent-incident-${item.id}`}
                className="bg-white rounded-lg shadow p-4 flex items-center gap-3 hover:shadow-md"
              >
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {item.incident.description}
                  </div>
                  <div className="text-xs text-gray-500">
                    Incident report · {formatDublinDate(item.incident.occurredAt)}
                  </div>
                </div>
                <IncidentSeverityChip severity={item.incident.severity} />
                <ArrowRightIcon className="h-4 w-4 text-gray-400" />
              </Link>
            ) : (
              <FeedItemCard
                key={`${item.kind}-${item.id}`}
                item={item.feed}
                showDate
                href={
                  item.kind === 'MOMENT'
                    ? `${base}/moments/${item.id}`
                    : `${base}/observations/${item.id}`
                }
              />
            )
          )}
        </div>
      )}
    </section>
  );
};

export default RecentPreview;
